import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { generatePONumber, formatAmount } from '@/components/campaign/utils/purchaseOrderUtils';
import { TermsAndConditions } from '@/components/campaign/components/purchase-order/TermsAndConditions';
import { DocumentsSection } from '@/components/campaign/components/purchase-order/DocumentsSection';
import { Plus, Trash2, Building2, MapPin, Calculator, FileSignature } from 'lucide-react';

const DRAFT_KEY = 'admin-po-draft';

interface LocationRow {
  id: string;
  name: string;
  devices: number;
  mediaOwner: string;
}

interface AdminPOCreatorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

interface DraftState {
  step: number;
  companyName: string;
  brandName: string;
  personInCharge: string;
  jobTitle: string;
  email: string;
  mobile: string;
  duration: number;
  locations: LocationRow[];
  costPerDevice: string;
  vatRate: string;
  baseTotalOverride: string;
  vatAmountOverride: string;
  totalAmountOverride: string;
  termsAccepted: boolean;
}

const defaultDraft: DraftState = {
  step: 0,
  companyName: '',
  brandName: '',
  personInCharge: '',
  jobTitle: '',
  email: '',
  mobile: '',
  duration: 30,
  locations: [{ id: crypto.randomUUID(), name: '', devices: 1, mediaOwner: '' }],
  costPerDevice: '25',
  vatRate: '15',
  baseTotalOverride: '',
  vatAmountOverride: '',
  totalAmountOverride: '',
  termsAccepted: false,
};

export const AdminPOCreatorDialog = ({ open, onOpenChange, onCreated }: AdminPOCreatorDialogProps) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const { language } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load draft from localStorage on mount
  const loadDraft = (): DraftState => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) return { ...defaultDraft, ...JSON.parse(saved) };
    } catch {}
    return defaultDraft;
  };

  const [draft, setDraftRaw] = useState<DraftState>(loadDraft);

  const setDraft = useCallback((updater: DraftState | ((prev: DraftState) => DraftState)) => {
    setDraftRaw(updater);
  }, []);

  const updateField = useCallback(<K extends keyof DraftState>(key: K, value: DraftState[K]) => {
    setDraft(prev => ({ ...prev, [key]: value }));
  }, [setDraft]);

  // Documents (not saved to draft)
  const [signature, setSignature] = useState<{ data: string; type: 'draw' | 'upload' } | null>(null);
  const [stamp, setStamp] = useState<string | null>(null);
  const [companyRegistration, setCompanyRegistration] = useState<File | null>(null);
  const [vatRegistration, setVatRegistration] = useState<File | null>(null);

  // Auto-save on tab switch
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [draft]);

  // Derived calculations
  const totalDevices = draft.locations.reduce((sum, l) => sum + (l.devices || 0), 0);
  const costPerDevicePerDay = parseFloat(draft.costPerDevice) || 0;
  const vatRateDecimal = (parseFloat(draft.vatRate) || 0) / 100;

  const autoBaseTotal = totalDevices * draft.duration * costPerDevicePerDay;
  const baseTotal = draft.baseTotalOverride ? parseFloat(draft.baseTotalOverride) || 0 : autoBaseTotal;

  const autoVat = baseTotal * vatRateDecimal;
  const vatAmount = draft.vatAmountOverride ? parseFloat(draft.vatAmountOverride) || 0 : autoVat;

  const autoTotal = baseTotal + vatAmount;
  const totalAmount = draft.totalAmountOverride ? parseFloat(draft.totalAmountOverride) || 0 : autoTotal;

  const addLocation = () => {
    updateField('locations', [...draft.locations, { id: crypto.randomUUID(), name: '', devices: 1, mediaOwner: '' }]);
  };

  const removeLocation = (id: string) => {
    if (draft.locations.length > 1) {
      updateField('locations', draft.locations.filter(l => l.id !== id));
    }
  };

  const updateLocation = (id: string, field: keyof LocationRow, value: string | number) => {
    updateField('locations', draft.locations.map(l => l.id === id ? { ...l, [field]: value } : l));
  };

  const resetForm = () => {
    setDraftRaw(defaultDraft);
    setSignature(null);
    setStamp(null);
    setCompanyRegistration(null);
    setVatRegistration(null);
    localStorage.removeItem(DRAFT_KEY);
  };

  const uploadFile = async (file: File, folder: string): Promise<string | null> => {
    const fileName = `${folder}/${Date.now()}-${file.name}`;
    const { data, error } = await supabase.storage
      .from('company_documents')
      .upload(fileName, file, { contentType: file.type });
    if (error) { console.error('Upload error:', error); return null; }
    const { data: urlData } = supabase.storage.from('company_documents').getPublicUrl(data.path);
    return urlData.publicUrl;
  };

  const handleSubmit = async () => {
    if (!signature) {
      toast({ title: 'Error', description: 'Signature is required', variant: 'destructive' });
      return;
    }
    if (!draft.companyName || !draft.email) {
      toast({ title: 'Error', description: 'Company name and email are required', variant: 'destructive' });
      return;
    }

    setIsSubmitting(true);
    try {
      const poNumber = generatePONumber();
      let companyRegUrl = '';
      let vatRegUrl = '';
      if (companyRegistration) companyRegUrl = (await uploadFile(companyRegistration, 'company-registration')) || '';
      if (vatRegistration) vatRegUrl = (await uploadFile(vatRegistration, 'vat-registration')) || '';

      const budgetCalculations = {
        baseTotal,
        vatAmount,
        subtotal: baseTotal,
        totalBudget: totalAmount,
        devices: totalDevices,
        duration: draft.duration,
        costPerDevicePerDay,
        vatRate: vatRateDecimal,
      };

      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;

      const { error: createError } = await supabase.functions.invoke('create-purchase-order', {
        body: {
          purchaseOrderNumber: poNumber,
          signatureData: signature.data,
          signatureType: signature.type,
          stampData: stamp,
          companyRegistrationUrl: companyRegUrl,
          vatRegistrationUrl: vatRegUrl,
          status: 'completed',
          budgetCalculations,
        },
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      if (createError) throw new Error(createError.message || 'Failed to create purchase order');

      await supabase.functions.invoke('send-purchase-order', {
        body: {
          poNumber,
          companyDetails: {
            companyName: draft.companyName,
            brandName: draft.brandName,
            personInCharge: draft.personInCharge,
            jobTitle: draft.jobTitle,
            email: draft.email,
            mobile: draft.mobile,
          },
          devices: totalDevices,
          duration: draft.duration,
          amount: totalAmount,
          signature,
          stamp: stamp || '',
          recipientEmail: 'admin@tableads.com',
          customerEmail: draft.email,
          companyRegistrationUrl: companyRegUrl,
          vatRegistrationUrl: vatRegUrl,
          selectedLocations: draft.locations.filter(l => l.name),
          budgetCalculations,
        },
      });

      toast({
        title: language === 'ar' ? 'تم الإنشاء' : 'Created',
        description: language === 'ar' ? `تم إنشاء أمر الشراء ${poNumber}` : `Purchase order ${poNumber} created successfully`,
      });

      resetForm();
      onOpenChange(false);
      onCreated();
    } catch (error: any) {
      console.error('PO creation error:', error);
      toast({ title: 'Error', description: error.message || 'Failed to create purchase order', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    { icon: Building2, label: language === 'ar' ? 'تفاصيل الشركة' : 'Company Details' },
    { icon: MapPin, label: language === 'ar' ? 'المواقع' : 'Locations' },
    { icon: Calculator, label: language === 'ar' ? 'الملخص المالي' : 'Financial Summary' },
    { icon: FileSignature, label: language === 'ar' ? 'المستندات والتوقيع' : 'Documents & Signature' },
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!isSubmitting) { onOpenChange(v); if (!v) { /* keep draft */ } } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {language === 'ar' ? 'إنشاء أمر شراء جديد' : 'Create New Purchase Order'}
          </DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center justify-between mb-6">
          {steps.map((s, i) => (
            <div key={i} className="flex items-center gap-1">
              <button
                onClick={() => updateField('step', i)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  i === draft.step
                    ? 'bg-primary text-primary-foreground'
                    : i < draft.step
                    ? 'bg-primary/20 text-primary'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                <s.icon className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{s.label}</span>
                <span className="sm:hidden">{i + 1}</span>
              </button>
              {i < steps.length - 1 && <div className="w-6 h-px bg-border hidden sm:block" />}
            </div>
          ))}
        </div>

        {/* Step 0: Company Details */}
        {draft.step === 0 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{language === 'ar' ? 'اسم الشركة' : 'Company Name'} *</Label>
                <Input value={draft.companyName} onChange={e => updateField('companyName', e.target.value)} />
              </div>
              <div>
                <Label>{language === 'ar' ? 'اسم العلامة التجارية' : 'Brand Name'}</Label>
                <Input value={draft.brandName} onChange={e => updateField('brandName', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{language === 'ar' ? 'المسؤول' : 'Person in Charge'}</Label>
                <Input value={draft.personInCharge} onChange={e => updateField('personInCharge', e.target.value)} />
              </div>
              <div>
                <Label>{language === 'ar' ? 'المسمى الوظيفي' : 'Job Title'}</Label>
                <Input value={draft.jobTitle} onChange={e => updateField('jobTitle', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{language === 'ar' ? 'البريد الإلكتروني' : 'Email'} *</Label>
                <Input type="email" value={draft.email} onChange={e => updateField('email', e.target.value)} />
              </div>
              <div>
                <Label>{language === 'ar' ? 'الجوال' : 'Mobile'}</Label>
                <Input value={draft.mobile} onChange={e => updateField('mobile', e.target.value)} />
              </div>
            </div>
          </div>
        )}

        {/* Step 1: Locations */}
        {draft.step === 1 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">{language === 'ar' ? 'المواقع' : 'Locations'}</Label>
              <Button variant="outline" size="sm" onClick={addLocation} className="gap-1">
                <Plus className="h-3.5 w-3.5" />
                {language === 'ar' ? 'إضافة موقع' : 'Add Location'}
              </Button>
            </div>
            {draft.locations.map((loc, idx) => (
              <Card key={loc.id}>
                <CardContent className="pt-4 pb-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">
                      {language === 'ar' ? `موقع ${idx + 1}` : `Location ${idx + 1}`}
                    </span>
                    {draft.locations.length > 1 && (
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeLocation(loc.id)}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">{language === 'ar' ? 'الاسم' : 'Name'}</Label>
                      <Input value={loc.name} onChange={e => updateLocation(loc.id, 'name', e.target.value)} placeholder="Location name" />
                    </div>
                    <div>
                      <Label className="text-xs">{language === 'ar' ? 'الأجهزة' : 'Devices'}</Label>
                      <Input type="number" min={1} value={loc.devices} onChange={e => updateLocation(loc.id, 'devices', parseInt(e.target.value) || 0)} />
                    </div>
                    <div>
                      <Label className="text-xs">{language === 'ar' ? 'مالك الوسائط' : 'Media Owner'}</Label>
                      <Input value={loc.mediaOwner} onChange={e => updateLocation(loc.id, 'mediaOwner', e.target.value)} placeholder="e.g. TableAds" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div>
                <Label>{language === 'ar' ? 'المدة (أيام)' : 'Duration (days)'}</Label>
                <Input type="number" min={1} value={draft.duration} onChange={e => updateField('duration', parseInt(e.target.value) || 1)} />
              </div>
              <div>
                <Label>{language === 'ar' ? 'إجمالي الأجهزة' : 'Total Devices'}</Label>
                <Input value={totalDevices} disabled />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Financial Summary */}
        {draft.step === 2 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{language === 'ar' ? 'تكلفة الجهاز/اليوم' : 'Cost per Device per Day (SAR)'}</Label>
                <Input type="number" min={0} step="0.01" value={draft.costPerDevice} onChange={e => updateField('costPerDevice', e.target.value)} />
              </div>
              <div>
                <Label>{language === 'ar' ? 'نسبة الضريبة %' : 'VAT Rate (%)'}</Label>
                <Input type="number" min={0} step="0.1" value={draft.vatRate} onChange={e => updateField('vatRate', e.target.value)} />
              </div>
            </div>

            <Card>
              <CardContent className="pt-4 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    {language === 'ar' ? 'المبلغ الأساسي' : `Base Total (${totalDevices} × ${draft.duration} × ${draft.costPerDevice})`}
                  </span>
                  <span className="text-muted-foreground">{language === 'ar' ? 'تلقائي' : 'Auto'}: {formatAmount(autoBaseTotal)} SAR</span>
                </div>
                <div>
                  <Label className="text-xs">{language === 'ar' ? 'تجاوز المبلغ الأساسي' : 'Override Base Total (leave blank for auto)'}</Label>
                  <Input type="number" min={0} step="0.01" value={draft.baseTotalOverride} onChange={e => updateField('baseTotalOverride', e.target.value)} placeholder={formatAmount(autoBaseTotal)} />
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    {language === 'ar' ? 'الضريبة' : `VAT (${draft.vatRate}%)`}
                  </span>
                  <span className="text-muted-foreground">{language === 'ar' ? 'تلقائي' : 'Auto'}: {formatAmount(autoVat)} SAR</span>
                </div>
                <div>
                  <Label className="text-xs">{language === 'ar' ? 'تجاوز الضريبة' : 'Override VAT Amount (leave blank for auto)'}</Label>
                  <Input type="number" min={0} step="0.01" value={draft.vatAmountOverride} onChange={e => updateField('vatAmountOverride', e.target.value)} placeholder={formatAmount(autoVat)} />
                </div>

                <div className="border-t pt-3 flex items-center justify-between text-sm font-semibold">
                  <span>{language === 'ar' ? 'المبلغ الإجمالي' : 'Total Amount'}</span>
                  <span>{language === 'ar' ? 'تلقائي' : 'Auto'}: {formatAmount(autoTotal)} SAR</span>
                </div>
                <div>
                  <Label className="text-xs">{language === 'ar' ? 'تجاوز الإجمالي' : 'Override Total Amount (leave blank for auto)'}</Label>
                  <Input type="number" min={0} step="0.01" value={draft.totalAmountOverride} onChange={e => updateField('totalAmountOverride', e.target.value)} placeholder={formatAmount(autoTotal)} />
                </div>

                <div className="border-t pt-3 text-right font-bold text-lg">
                  {formatAmount(totalAmount)} SAR
                </div>
              </CardContent>
            </Card>

            <TermsAndConditions />
            <div className="flex items-center gap-2">
              <input type="checkbox" id="terms" checked={draft.termsAccepted} onChange={e => updateField('termsAccepted', e.target.checked)} className="rounded" />
              <Label htmlFor="terms" className="text-sm cursor-pointer">
                {language === 'ar' ? 'أوافق على الشروط والأحكام' : 'I accept the terms and conditions'}
              </Label>
            </div>
          </div>
        )}

        {/* Step 3: Documents & Signature */}
        {draft.step === 3 && (
          <DocumentsSection
            signature={signature}
            stamp={stamp}
            companyRegistration={companyRegistration}
            vatRegistration={vatRegistration}
            onSignatureSave={(data, type) => setSignature({ data, type })}
            onStampUpload={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                const reader = new FileReader();
                reader.onloadend = () => setStamp(reader.result as string);
                reader.readAsDataURL(file);
              }
            }}
            onStampRemove={() => setStamp(null)}
            onCompanyRegistrationUpload={(e) => { const file = e.target.files?.[0]; if (file) setCompanyRegistration(file); }}
            onVATRegistrationUpload={(e) => { const file = e.target.files?.[0]; if (file) setVatRegistration(file); }}
          />
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button variant="outline" onClick={() => updateField('step', draft.step - 1)} disabled={draft.step === 0}>
            {language === 'ar' ? 'السابق' : 'Back'}
          </Button>
          {draft.step < steps.length - 1 ? (
            <Button onClick={() => updateField('step', draft.step + 1)}>
              {language === 'ar' ? 'التالي' : 'Next'}
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={isSubmitting || !signature || !draft.companyName || !draft.email || !draft.termsAccepted}>
              {isSubmitting
                ? (language === 'ar' ? 'جاري الإنشاء...' : 'Creating...')
                : (language === 'ar' ? 'إنشاء أمر الشراء' : 'Create Purchase Order')}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
